import type { PaymentMethodResponse, PaymentPeriod } from '@kimuno/api-sdk';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getSubscription, selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import { NotificationContext } from '../../state/NotificationContextProvider';
import EditableSection from '../EditableSection';
import PaymentInfoRenderer from './PaymentInfoRenderer';
import PaymentRhythm from './PaymentRhythm';

export interface ChangeRhythmPayload {
	paymentRhythm: string;
}

const PaymentInfo = () => {
	const { t } = useTranslation();
	const user = useSelector(selectUser);
	const subscription = useSelector(getSubscription);
	const { showNotification } = useContext(NotificationContext);
	const [isShowAlert, setIsShowAlert] = useState<boolean>(false);

	const { getValues, control } = useForm<ChangeRhythmPayload>({
		mode: 'onBlur',
	});

	const showPaymentInformation =
		user?.isPremium && !user?.isFree && !user?.isReadOnlyLicence && !!user?.paymentMethod;

	if (!showPaymentInformation) return null;

	const paymentRhythmOptions = subscription?.paymentPeriod?.possibleValues || [];

	const currentPaymentMethod = user?.paymentMethod;
	const currentPaymentRhythm = subscription?.paymentPeriod?.type as PaymentPeriod;

	const onSaveChanges = async ({ toggleEditMode }: { toggleEditMode: () => void }) => {
		const values = getValues();
		const newRhythm = values.paymentRhythm;
		const isRhythmChanged = currentPaymentRhythm !== newRhythm;

		try {
			if (newRhythm && isRhythmChanged) {
				await services.billing.updatePaymentPeriod(newRhythm as PaymentPeriod);
				showNotification({
					message: t('All changes were saved successfully'),
					severity: 'success',
				});
				setIsShowAlert(true);
			}
			toggleEditMode();
		} catch {
			showNotification({
				message: t('An error occurred while saving changes'),
				severity: 'error',
			});
		}
	};

	return (
		<div data-testid="payment-rhythm-section">
			<EditableSection title={t('Payment')} onSaveChanges={onSaveChanges}>
				{({ isEditMode }) => (
					<PaymentInfoRenderer
						isEditMode={isEditMode}
						currentPaymentMethod={currentPaymentMethod as PaymentMethodResponse}
					>
						<PaymentRhythm
							isEditMode={isEditMode}
							isShowAlert={isShowAlert}
							availablePaymentRhythm={paymentRhythmOptions}
							currentPaymentRhythm={currentPaymentRhythm}
							control={control}
						/>
					</PaymentInfoRenderer>
				)}
			</EditableSection>
		</div>
	);
};

export default PaymentInfo;
